import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { toast } from "sonner";
import { Plus, X, Edit3, Trash2, Search, Mail, Phone, Building2, Tag } from "lucide-react";

const AVAIL = {
  available: { label: "Available", color: "#00C781" },
  busy:      { label: "Busy",      color: "#EF4444" },
  away:      { label: "Away",      color: "#F59E0B" },
  unknown:   { label: "",          color: "#3A3A3A" },
};
const COLORS = ["#5A90F5","#FF8A00","#A855F7","#00C781","#EF4444","#14B8A6","#F59E0B","#EC4899"];

function initials(name) { return name?.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2) || "?"; }

function ContactForm({ initial, onClose, onSave }) {
  const [f, setF] = useState({ name:"", email:"", phone:"", company:"", role:"", notes:"", availability:"unknown", color:"#5A90F5", ...initial });
  return (
    <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111] border border-[#2A2A2A] rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e=>e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-[#EDEDED] mb-4">{initial?"Edit":"New"} contact</h3>
        {[["name","Name *"],["email","Email"],["phone","Phone"],["company","Company"],["role","Role"]].map(([k,p]) => (
          <input key={k} value={f[k]||""} onChange={e=>setF(x=>({...x,[k]:e.target.value}))} placeholder={p}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none mb-2.5 focus:border-[#5A90F5]" />
        ))}
        <div className="flex gap-3 mb-3">
          <div className="flex-1">
            <label className="text-[10px] text-[#52525B] mb-1 block">Availability</label>
            <select value={f.availability} onChange={e=>setF(x=>({...x,availability:e.target.value}))}
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl px-3 py-2 text-[12px] text-[#EDEDED] outline-none">
              {Object.entries(AVAIL).map(([k,v])=><option key={k} value={k}>{v.label||"Unknown"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-[#52525B] mb-1 block">Color</label>
            <div className="flex flex-wrap gap-1 pt-1">
              {COLORS.map(c=><button key={c} onClick={()=>setF(x=>({...x,color:c}))} className="w-5 h-5 rounded-full border-2 transition-all" style={{background:c,borderColor:f.color===c?"#fff":"transparent"}}/>)}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-[12px] border border-[#2A2A2A] text-[#52525B]">Cancel</button>
          <button onClick={()=>{if(f.name.trim())onSave(f);}} disabled={!f.name.trim()} className="flex-1 py-2 rounded-xl text-[12px] font-semibold bg-[#5A90F5] text-white disabled:opacity-30">Save</button>
        </div>
      </div>
    </div>
  );
}

export default function Directory() {
  const { current } = useWorkspace();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [health, setHealth] = useState(null);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    try { const { data } = await api.get(`/workspaces/${wsId}/contacts`); setContacts(data); } catch {}
  }, [wsId]);

  const selectContact = async (c) => {
    setSelected(c);
    setHealth(null);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/contacts/${c.contact_id}/health`);
      setHealth(data);
    } catch {}
  };

  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try { const { data: c } = await api.post(`/workspaces/${wsId}/contacts`, data); setContacts(x=>[...x,c].sort((a,b)=>a.name.localeCompare(b.name))); setShowForm(false); } catch { toast.error("Failed"); }
  };
  const update = async (data) => {
    try { const { data: c } = await api.put(`/workspaces/${wsId}/contacts/${editing.contact_id}`, data); setContacts(x=>x.map(y=>y.contact_id===c.contact_id?c:y)); setEditing(null); if(selected?.contact_id===c.contact_id)setSelected(c); } catch {}
  };
  const del = async (id) => {
    if(!window.confirm("Delete this contact?"))return;
    try { await api.delete(`/workspaces/${wsId}/contacts/${id}`); setContacts(x=>x.filter(y=>y.contact_id!==id)); if(selected?.contact_id===id)setSelected(null); } catch {}
  };
  const updateAvail = async (id, avail) => {
    try { const { data: c } = await api.put(`/workspaces/${wsId}/contacts/${id}`, { availability: avail }); setContacts(x=>x.map(y=>y.contact_id===id?c:y)); if(selected?.contact_id===id)setSelected(c); } catch {}
  };

  const filtered = contacts.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.company?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="flex h-full bg-[#0A0A0A]">
      {/* List */}
      <div className="w-72 flex-shrink-0 border-r border-[#1A1A1A] flex flex-col">
        <div className="px-4 py-4 border-b border-[#1A1A1A] flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-[#3A3A3A] flex-shrink-0" />
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search contacts..."
            className="flex-1 bg-transparent text-[12px] text-[#EDEDED] placeholder-[#3A3A3A] outline-none" />
          <button onClick={()=>setShowForm(true)} className="flex-shrink-0 w-6 h-6 rounded-md bg-[#1a1a1a] hover:bg-[#2A2A2A] flex items-center justify-center text-[#52525B] hover:text-white transition-colors">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center"><p className="text-[11px] text-[#3A3A3A]">{search?"No results":"No contacts yet"}</p></div>
          ) : filtered.map(c => (
            <button key={c.contact_id} onClick={()=>selectContact(c)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#0F0F0F] transition-colors text-left ${selected?.contact_id===c.contact_id?"bg-[#141414]":""}`}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{background:c.color+"30",color:c.color}}>{initials(c.name)}</div>
              <div className="min-w-0 flex-1">
                <p className="text-[12px] font-medium text-[#EDEDED] truncate">{c.name}</p>
                <p className="text-[10px] text-[#52525B] truncate">{c.role||c.company||c.email||""}</p>
              </div>
              {c.availability !== "unknown" && <div className="w-2 h-2 rounded-full flex-shrink-0" style={{background:AVAIL[c.availability]?.color}} />}
            </button>
          ))}
        </div>
      </div>

      {/* Detail */}
      <div className="flex-1 min-w-0 overflow-y-auto">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center"><Building2 className="w-5 h-5 text-[#3A3A3A]"/></div>
            <p className="text-[13px] text-[#3A3A3A]">Select a contact</p>
          </div>
        ) : (
          <div className="p-8 max-w-xl">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-[20px] font-bold" style={{background:selected.color+"25",color:selected.color}}>{initials(selected.name)}</div>
                <div>
                  <h2 className="text-[20px] font-bold text-[#EDEDED]">{selected.name}</h2>
                  {selected.role && <p className="text-[13px] text-[#52525B]">{selected.role}{selected.company?` · ${selected.company}`:""}</p>}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={()=>setEditing(selected)} className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA]"><Edit3 className="w-3.5 h-3.5"/></button>
                <button onClick={()=>del(selected.contact_id)} className="p-2 rounded-xl hover:bg-[#EF4444]/10 text-[#52525B] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
            {/* Availability */}
            <div className="mb-4">
              <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-2">Availability</p>
              <div className="flex gap-2">
                {Object.entries(AVAIL).filter(([k])=>k!=="unknown").map(([k,v])=>(
                  <button key={k} onClick={()=>updateAvail(selected.contact_id,k)}
                    className={`px-3 py-1.5 rounded-xl text-[11px] border transition-colors ${selected.availability===k?"border-transparent text-white":"border-[#2A2A2A] text-[#52525B] hover:text-[#A1A1AA]"}`}
                    style={selected.availability===k?{background:v.color}:{}}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Info */}
            <div className="space-y-2">
              {selected.email && <div className="flex items-center gap-3 p-3 bg-[#111] rounded-xl border border-[#2A2A2A]"><Mail className="w-3.5 h-3.5 text-[#52525B]"/><a href={`mailto:${selected.email}`} className="text-[12px] text-[#5A90F5] hover:underline">{selected.email}</a></div>}
              {selected.phone && <div className="flex items-center gap-3 p-3 bg-[#111] rounded-xl border border-[#2A2A2A]"><Phone className="w-3.5 h-3.5 text-[#52525B]"/><span className="text-[12px] text-[#EDEDED]">{selected.phone}</span></div>}
              {selected.company && <div className="flex items-center gap-3 p-3 bg-[#111] rounded-xl border border-[#2A2A2A]"><Building2 className="w-3.5 h-3.5 text-[#52525B]"/><span className="text-[12px] text-[#EDEDED]">{selected.company}</span></div>}
            </div>

            {/* Relationship Health */}
            {health && (
              <div className="mt-4 p-4 bg-[#111] rounded-xl border border-[#2A2A2A]">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-semibold text-[#EDEDED]">Relationship Health</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                    health.health_label === "Strong" ? "bg-[#00C781]/15 text-[#00C781]" :
                    health.health_label === "Needs attention" ? "bg-[#F59E0B]/15 text-[#F59E0B]" :
                    "bg-[#EF4444]/15 text-[#EF4444]"
                  }`}>{health.health_label}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
                    <p className="text-[16px] font-bold text-[#EDEDED]">{health.commitments_made}</p>
                    <p className="text-[9px] text-[#71717A]">promises</p>
                  </div>
                  <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
                    <p className="text-[16px] font-bold text-[#00C781]">{health.kept_rate}%</p>
                    <p className="text-[9px] text-[#71717A]">kept rate</p>
                  </div>
                  <div className="text-center p-2 bg-[#0A0A0A] rounded-lg">
                    <p className="text-[16px] font-bold text-[#EF4444]">{health.commitments_broken}</p>
                    <p className="text-[9px] text-[#71717A]">broken</p>
                  </div>
                </div>
                {health.active_commitments > 0 && (
                  <p className="text-[10px] text-[#A1A1AA]">{health.active_commitments} active commitment{health.active_commitments !== 1 ? "s" : ""} with this contact</p>
                )}
                {health.last_interaction && (
                  <p className="text-[10px] text-[#71717A] mt-1">Last interaction: {new Date(health.last_interaction).toLocaleDateString([], {month:"short",day:"numeric",year:"numeric"})}</p>
                )}
              </div>
            )}
            {selected.notes && <div className="mt-4 p-4 bg-[#111] rounded-xl border border-[#2A2A2A]"><p className="text-[11px] text-[#52525B] mb-1">Notes</p><p className="text-[12px] text-[#A1A1AA] whitespace-pre-wrap">{selected.notes}</p></div>}
          </div>
        )}
      </div>
      {showForm && <ContactForm onClose={()=>setShowForm(false)} onSave={create}/>}
      {editing && <ContactForm initial={editing} onClose={()=>setEditing(null)} onSave={update}/>}
    </div>
  );
}
